import { css } from "@compiled/react";
import { DEFAULT_VALUES, useLocalizedHref } from "@libraries/web-framework";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";

import HeroImageTextSide from "@/patterns/wpl/hero-image-text-side/src";

import { getHeroSectionImage } from "../localized-images";

// Styles for adding a Lifted Bar
// const adBannerContainerStyles = css`
//     margin: auto;
//     position: relative;
//     background: #091e42;
//     display: flex;
//     justify-content: center;

//     ${media.above.sm} {
//         img {
//             display: block;
//         }
//     }

//     ${media.above.md} {
//         img {
//             display: unset;
//         }
//     }
// `;

const jswHeroSectionStyle = css({
    ".jsw-hero-section": {
        "overflow": "hidden",
        "padding": "110px 16px 30px",

        ".row-layout": {
            maxWidth: "1440px",
            width: "auto",
            padding: "0"
        },

        ".content-block": {
            order: "2"
        },

        "h1": {
            "fontSize": "2rem",
            "lineHeight": "1.25",
            "marginBlockEnd": "14px",
            "-webkit-font-smoothing": "antialiased",
            "-moz-osx-font-smoothing": "grayscale"
        },

        "p": {
            "fontSize": "1.25rem",
            "letterSpacing": "0.3px",
            "lineHeight": "1.4",
            "marginBlockEnd": "24px",
            "-webkit-font-smoothing": "antialiased",
            "-moz-osx-font-smoothing": "grayscale"
        },

        "a": {
            "-webkit-font-smoothing": "antialiased",
            "-moz-osx-font-smoothing": "grayscale"
        },

        ".contentBlock-linkFlex": {
            marginBlockEnd: "0"
        },

        "img": {
            order: "1",
            width: "100%",
            maxWidth: "none"
        }
    },

    [media.only.md]: {
        ".jsw-hero-section": {
            ".row-layout": {
                gridTemplateColumns: "minmax(0, 1fr) minmax(0, 0.95fr)",
                gap: "0"
            },

            ".content-block": {
                order: "1",
                marginInlineStart: "16.5%",
                width: "78%"
            },

            "h1": {
                fontSize: "2.75rem",
                lineHeight: "1.18181818",
                marginBlockEnd: "16px"
            },

            "p": {
                fontSize: "1.25rem",
                letterSpacing: "0.3px",
                lineHeight: "1.4",
                marginBlockEnd: "24px"
            },

            "img": {
                width: "850px",
                order: "2",
                alignSelf: "baseline"
            }
        }
    },

    [media.only.sm]: {
        ".jsw-hero-section": {
            ".row-layout": {
                gridTemplateColumns: "minmax(0, 1fr) minmax(0, 0.95fr)",
                gap: "0"
            },
            ".content-block": {
                order: "1",
                marginInlineStart: "0",
                width: "95%"
            },

            "img": {
                width: "118%",
                order: "2",
                alignSelf: "baseline"
            },
            "h1": {
                fontSize: "2.75rem",
                lineHeight: "1.18181818",
                marginBlockEnd: "16px"
            },
            "p": {
                fontSize: "1.25rem",
                letterSpacing: "0.3px",
                lineHeight: "1.4",
                marginBlockEnd: "24px"
            }
        }
    },

    "@media (max-width: 1300px) and (min-width: 1024px)": {
        ".jsw-hero-section img": {
            width: "147%"
        }
    },

    [media.only.lg]: {
        ".jsw-hero-section": {
            ".row-layout": {
                gridTemplateColumns: "minmax(0, 1fr) minmax(0, 0.95fr)",
                gap: "0",
                paddingInlineStart: "16px",
                paddingInlineEnd: "16px"
            },
            ".content-block": {
                order: "1",
                marginInlineStart: "16.5%",
                width: "78%"
            },
            "img": {
                width: "850px",
                order: "2",
                alignSelf: "baseline"
            },

            "h1": {
                fontSize: "3rem",
                lineHeight: "3.5rem",
                marginBlockEnd: "24px"
            },

            "p": {
                fontSize: "1.25rem",
                letterSpacing: "0.3px",
                lineHeight: "1.4"
            }
        }
    },
    [media.only.xl]: {
        ".jsw-hero-section": {
            ".content-block": {
                order: "1",
                marginInlineStart: "16.5%",
                width: "78%"
            },
            ".row-layout": {
                gridTemplateColumns: "minmax(0, 1fr) minmax(0, 0.95fr)",
                gap: "0",
                paddingInlineStart: "16px",
                paddingInlineEnd: "16px"
            },

            "img": {
                width: "850px",
                order: "2",
                alignSelf: "baseline"
            },

            "h1": {
                fontSize: "3rem",
                lineHeight: "3.5rem",
                marginBlockEnd: "24px"
            },

            "p": {
                fontSize: "1.25rem",
                letterSpacing: "0.3px",
                lineHeight: "1.4"
            }
        }
    }
});

const HeroSection = () => {
    const intl = useIntl();

    const localizedImageSrc = getHeroSectionImage(intl.locale);

    return (
        <section css={jswHeroSectionStyle}>
            <HeroImageTextSide
                headerText={
                    <FormattedMessage
                        id="JiraSoftwarePageTemplate.heroHeader"
                        defaultMessage="Move fast, stay aligned, and build better - together"
                        values={DEFAULT_VALUES}
                    />
                }
                bodyText={
                    <FormattedMessage
                        id="JiraSoftwarePageTemplate.heroBody"
                        defaultMessage="The #1 software development tool used by agile teams"
                        values={DEFAULT_VALUES}
                    />
                }
                ctaProps={{
                    buttonProps: {
                        text: intl.formatMessage({
                            id: "JiraSoftwarePageTemplate.heroCTA",
                            defaultMessage: "Get it free"
                        }),
                        color: "yellow",
                        url: useLocalizedHref("/software/jira/free"),
                        dataEventProps: {
                            "data-event-component": "linkButton",
                            "data-event-container": "heroSection",
                            "data-event": "clicked",
                            "data-product-key": "jiraSoftware"
                        }
                    }
                }}
                className={"jsw-hero-section"}
                // TODO: replace with locally stored asset in the repo or content platform?
                imageLink={localizedImageSrc}
                imageAltText={intl.formatMessage({
                    id: "JiraSoftwarePageTemplate.heroImageAltText",
                    defaultMessage:
                        "Illustration demonstrating workflows in Jira Software"
                })}
                backgroundProperties={{
                    bgColor: "#253858",
                    textColor: "#ffffff",
                    bgColor2: "#091e42",
                    gradientDirection: "180"
                }}
                aspectRatio={"none"}
            />
            {/* To use a Lifted Bar:
            
            <div css={adBannerContainerStyles}>
                <UnleashBanner />
            </div>

             */}
        </section>
    );
};

export default HeroSection;
